import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ImageOff, Heart, Leaf } from 'lucide-react';
import QuantityStepper from './QuantityStepper';
import { FAVOURITE_AUTH_REQUIRED, useFavourites } from '../context/FavouritesContext';
import { useToast } from '../context/ToastContext';
import { btnPrimary, btnXs, focusRing, itemCard } from '../styles/ui';

// Pure presentational card except for favourites, which reads shared context
// directly (rather than taking more props) since every card needs the same
// heart-toggle behaviour and FavouritesProvider already guards guest access.
const ProductCard = ({ product, quantity = 0, onAddToCart, onSetQuantity }) => {
  const {
    _id,
    sku,
    displayName,
    thumbnail,
    price,
    mrp,
    discountPercentage,
    sellingUom,
    isInStock,
    catchWeight,
  } = product;

  const location = useLocation();
  const navigate = useNavigate();
  const { isFavourite, toggleFavourite } = useFavourites();
  const { showToast } = useToast();
  const favourited = isFavourite(_id);

  const handleToggleFavourite = async () => {
    try {
      const wasFavourited = favourited;
      const result = await toggleFavourite(_id, product);
      if (result === FAVOURITE_AUTH_REQUIRED) {
        const redirect = `${location.pathname}${location.search}${location.hash}`;
        navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
        return;
      }
      showToast(wasFavourited ? 'Removed from favourites' : 'Added to favourites');
    } catch {
      // Best-effort — FavouritesContext already rolls back the optimistic
      // update on failure, nothing further to surface on a product card.
    }
  };

  const handleAddToCart = () => {
    onAddToCart(product);
    showToast(`${displayName} added to cart`);
  };

  return (
    <div className={`card-hover ${itemCard} overflow-hidden flex flex-col`}>
      <div className="aspect-square bg-gradient-to-br from-emerald-50 to-emerald-100/50 relative overflow-hidden">
        <Link to={`/products/${sku || _id}`} className={`absolute inset-0 block ${focusRing}`}>
          {thumbnail ? (
            <img
              src={thumbnail}
              alt={displayName}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-emerald-300">
              <ImageOff size={32} aria-hidden="true" />
            </div>
          )}
          {!isInStock && (
            <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
              <span className="bg-gray-800 text-white text-xs font-semibold px-3 py-1 rounded-full">Out of stock</span>
            </div>
          )}
        </Link>

        <div className="absolute top-2 left-2 flex flex-col gap-1.5 items-start pointer-events-none">
          {discountPercentage > 0 && (
            <span className="bg-gradient-accent text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-glow">
              {discountPercentage}% OFF
            </span>
          )}
          {isInStock && (
            <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-emerald-700 text-[10px] font-semibold px-2 py-1 rounded-full shadow-soft">
              <Leaf size={10} aria-hidden="true" /> Fresh
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={handleToggleFavourite}
          aria-label={favourited ? 'Remove from favourites' : 'Add to favourites'}
          aria-pressed={favourited}
          className={`absolute z-10 top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow-soft flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 ${focusRing}`}
        >
          <Heart
            size={16}
            className={favourited ? 'text-accent-500 fill-accent-500' : 'text-gray-400'}
            aria-hidden="true"
          />
        </button>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <Link to={`/products/${sku || _id}`} className={focusRing}>
          <h3 className="font-bold text-emerald-950 text-sm leading-tight mb-1 line-clamp-2">{displayName}</h3>
        </Link>
        {catchWeight && <p className="text-xs text-accent-600 mb-1">Weight may vary — Pay on Delivery only</p>}

        <div className="mt-auto pt-2.5 border-t border-emerald-50 flex items-center justify-between gap-2">
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className="font-bold text-emerald-950">₹{price ?? '—'}</span>
              {mrp != null && mrp > price && (
                <span className="text-xs text-gray-400 line-through">₹{mrp}</span>
              )}
            </div>
            {sellingUom && (
              <span className="inline-block mt-0.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-md">
                {sellingUom}
              </span>
            )}
          </div>

          {isInStock &&
            (quantity > 0 ? (
              <QuantityStepper quantity={quantity} onChange={(qty) => onSetQuantity(_id, qty)} />
            ) : (
              <button
                type="button"
                onClick={handleAddToCart}
                className={`${btnPrimary} ${btnXs}`}
              >
                Add
              </button>
            ))}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
